import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Lock, Sparkles, Loader2, Trash2, ChevronDown, ChevronUp, Star,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  afriListSubmissions, afriRejectSubmission, afriLockTask, afriRankTask,
} from "@/lib/afritask";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const displayNameOf = (u) => u.display_name || u.full_name || u.username;
const isLegacyUser = (u) => !u.display_name && !u.full_name;

// ----- Per-type submission renderer ----------------------------------------
function SubmissionBody({ taskType, data }) {
  const [expanded, setExpanded] = useState(false);
  if (!data) {
    return <p className="text-xs italic text-neutral-400">No submission payload</p>;
  }

  if (taskType === "prompt_rating" || taskType === "voice_rating") {
    const overall = data.overall;
    const ratings = data.ratings || {};
    return (
      <div className="space-y-2">
        {overall != null && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-neutral-700">Overall:</span>
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`h-3.5 w-3.5 ${n <= overall ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`}
                />
              ))}
            </span>
            <span className="tabular-nums text-neutral-500">{overall}/5</span>
          </div>
        )}
        {Object.keys(ratings).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(ratings).map(([crit, v]) => (
              <Badge
                key={crit}
                variant="outline"
                className="text-[10px] font-normal text-neutral-700"
              >
                {crit}: <span className="ml-1 font-semibold tabular-nums">{v}/5</span>
              </Badge>
            ))}
          </div>
        )}
        {data.reason && (
          <p className="text-xs text-neutral-600">
            <span className="font-medium">Reason:</span> {data.reason}
          </p>
        )}
      </div>
    );
  }

  if (taskType === "image_labeling") {
    const tags = data.tags || [];
    return (
      <div className="space-y-2">
        {data.image_url && (
          <img
            src={data.image_url}
            alt="submission"
            className="max-h-40 rounded border border-neutral-200"
          />
        )}
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag, i) => (
            <Badge key={i} variant="secondary" className="text-[10px] font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  if (taskType === "translation" || taskType === "audio_transcription") {
    const text = data.translation || data.transcription || "";
    const long = text.length > 400;
    const shown = long && !expanded ? text.slice(0, 400) + "…" : text;
    return (
      <div className="space-y-1">
        <pre className="whitespace-pre-wrap break-words rounded bg-neutral-50 px-3 py-2 font-mono text-xs text-neutral-800">
          {shown}
        </pre>
        {taskType === "audio_transcription" && data.audio_url && (
          <audio controls src={data.audio_url} className="mt-1 h-8 w-full" />
        )}
        {long && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-700 hover:underline"
          >
            {expanded ? (
              <>Show less <ChevronUp className="h-3 w-3" /></>
            ) : (
              <>Show more <ChevronDown className="h-3 w-3" /></>
            )}
          </button>
        )}
      </div>
    );
  }

  if (taskType === "text_classification") {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-neutral-600">Label:</span>
        <Badge className="bg-amber-100 font-mono text-amber-900 hover:bg-amber-100">
          {data.selected_label || "—"}
        </Badge>
      </div>
    );
  }

  // Fallback — raw JSON pretty-print
  return (
    <pre className="whitespace-pre-wrap break-words rounded bg-neutral-50 px-3 py-2 font-mono text-[11px] text-neutral-700">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

// ============================================================ main component
export default function AdminTaskSubmissions() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectBusy, setRejectBusy] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [rankBusy, setRankBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await afriListSubmissions(taskId);
      setTask(r.task);
      setEntries(r.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const isOpen = task?.status === "open";
  const isLocked = task?.status === "locked" || task?.status === "awaiting_ranking";
  const cap = task ? task.entry_cap || task.applicant_cap || 0 : 0;
  const submittedCount = entries.length;

  const doReject = async () => {
    if (!rejectTarget) return;
    setRejectBusy(true);
    try {
      await afriRejectSubmission(taskId, rejectTarget.entry_id);
      toast.success("Submission rejected");
      setRejectTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    } finally {
      setRejectBusy(false);
    }
  };

  const doLock = async () => {
    setLockBusy(true);
    try {
      await afriLockTask(taskId);
      toast.success("Round locked — ready to rank");
      setLockOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock task");
    } finally {
      setLockBusy(false);
    }
  };

  const doRank = async () => {
    setRankBusy(true);
    try {
      await afriRankTask(taskId);
      toast.success("Ranking started — review results in the Ranked tab");
      setRankOpen(false);
      // Navigate back to AdminAfriSelection where the Ranked tab lives
      navigate("/admin/afriselection?tab=ranked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start ranking");
      setRankBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-14">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (!task) {
    return (
      <div className="py-14 text-center text-sm text-neutral-500">
        Task not found.{" "}
        <Link to="/admin/afriselection" className="font-medium text-emerald-700 hover:underline">
          Back to AfriSelection
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---------- Sticky header ---------- */}
      <div
        className="sticky top-0 z-10 -mx-4 border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur"
        data-testid="submissions-header"
      >
        <Link
          to="/admin/afriselection"
          className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-800"
          data-testid="submissions-back-link"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Awaiting Rank
        </Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-semibold text-neutral-900">{task.title}</h1>
              <Badge variant="outline" className="text-[10px]">{task.task_type}</Badge>
              {isOpen && (
                <span
                  className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
                  data-testid="submissions-pill-open"
                >
                  Still open
                </span>
              )}
              {isLocked && (
                <span
                  className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-600 ring-1 ring-inset ring-neutral-200"
                  data-testid="submissions-pill-locked"
                >
                  Locked
                </span>
              )}
            </div>
            <p className="mt-0.5 font-mono text-[10px] text-neutral-400" data-testid="submissions-taskid">
              {task.task_id}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              <span className="tabular-nums font-medium text-neutral-800">
                {task.entered_count || 0}
              </span>{" / "}
              <span className="tabular-nums">{cap}</span> entered ·{" "}
              <span
                className="tabular-nums font-medium text-neutral-800"
                data-testid="submissions-count"
              >
                {submittedCount}
              </span>{" "}
              submitted · Reward: {task.winners_count} × {task.winner_reward_points} pts (₦
              {(task.winner_reward_points || 0) * 10}) · Min score: {task.min_winning_score ?? 100}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {isOpen && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLockOpen(true)}
                data-testid="submissions-lock-btn"
              >
                <Lock className="mr-1.5 h-3.5 w-3.5" /> Lock now
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setRankOpen(true)}
              disabled={isOpen || submittedCount === 0}
              title={
                isOpen
                  ? "Lock the round first"
                  : submittedCount === 0
                  ? "No submissions to rank"
                  : "Run AI grading"
              }
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submissions-rank-btn"
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Run AI ranking
            </Button>
          </div>
        </div>
      </div>

      {/* ---------- Submissions list ---------- */}
      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-neutral-500">
            No submissions yet. Users who entered the round will appear here once they submit
            their work.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map((e, idx) => {
            const legacy = isLegacyUser(e);
            return (
              <Card key={e.entry_id} data-testid={`submission-row-${e.entry_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium tabular-nums text-neutral-400">
                          #{idx + 1}
                        </span>
                        <span
                          className="text-sm font-medium text-neutral-900"
                          data-testid={`submission-name-${e.entry_id}`}
                        >
                          {displayNameOf(e)}
                        </span>
                        <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[10px] text-neutral-500">
                          {e.username}
                        </span>
                        {legacy && (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                            legacy
                          </span>
                        )}
                        <span
                          className="text-[10px] text-neutral-400"
                          title={e.submitted_at}
                          data-testid={`submission-time-${e.entry_id}`}
                        >
                          {fmtRelative(e.submitted_at)}
                        </span>
                      </div>
                      <div className="mt-2">
                        <SubmissionBody
                          taskType={task.task_type}
                          data={e.submission_data}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRejectTarget(e)}
                      className="shrink-0 rounded p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                      title="Reject this submission"
                      data-testid={`submission-reject-btn-${e.entry_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ---------- Reject confirmation ---------- */}
      <AlertDialog
        open={!!rejectTarget}
        onOpenChange={(v) => !v && !rejectBusy && setRejectTarget(null)}
      >
        <AlertDialogContent data-testid="reject-submission-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              {rejectTarget && (
                <>
                  <span className="font-medium text-neutral-900">
                    {displayNameOf(rejectTarget)}
                  </span>{" "}
                  <span className="font-mono text-xs text-neutral-500">
                    {rejectTarget.username}
                  </span>
                  <br />
                  The user&apos;s entry will be marked as <b>rejected</b> and they won&apos;t be considered
                  for winners.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rejectBusy} data-testid="reject-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={rejectBusy}
              onClick={doReject}
              className="bg-red-600 hover:bg-red-700"
              data-testid="reject-confirm"
            >
              {rejectBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- Lock confirmation ---------- */}
      <AlertDialog open={lockOpen} onOpenChange={(v) => !v && !lockBusy && setLockOpen(false)}>
        <AlertDialogContent data-testid="lock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lock this round?</AlertDialogTitle>
            <AlertDialogDescription>
              No more entries will be accepted. Currently{" "}
              <b>{task.entered_count || 0}/{cap}</b> entered, <b>{submittedCount}</b> submitted.
              You can rank the round right after locking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lockBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={lockBusy}
              onClick={doLock}
              data-testid="lock-confirm"
            >
              {lockBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-3.5 w-3.5" />}
              Lock now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- Run-ranking confirmation ---------- */}
      <AlertDialog open={rankOpen} onOpenChange={(v) => !v && !rankBusy && setRankOpen(false)}>
        <AlertDialogContent data-testid="rank-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Run AI ranking on {submittedCount} submissions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will grade every submission using AfriSelection AI (uses LLM credits). The
              task will move to the <b>Ranked</b> tab where you can review scores and accept
              winners.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rankBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={rankBusy}
              onClick={doRank}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="rank-confirm"
            >
              {rankBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
              Run AI ranking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
